import React, { useState, useEffect, useCallback } from "react";
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Chip,
    Divider,
    Grid,
    Avatar,
    Tooltip,
    Paper,
    Button,
    Switch,
    FormControlLabel,
    Snackbar,
    Alert,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import {
    Close as CloseIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ContentCopy as CloneIcon,
    Image as ImageIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    ContentCopy as CopyIcon,
    Inventory as InventoryIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    TrendingUp,
    TrendingDown,
    SwapVert,
    OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { Product } from "../../types";
import { ProductImage } from "../../types/productImage";
import { formatDateShort } from "../../utils/date";
import {
    StockAdjustmentDialog,
    StockAdjustmentResult,
} from "../inventory/StockAdjustmentDialog";
import { StockReservationsDialog } from "../inventory/StockReservationsDialog";
import {
    stockService,
    StockMovement,
} from "../../services/stockService";
import { productService } from "../../services/productService";
import { AuditTrail } from "../common/AuditTrail";
import { usePermissions } from "../../hooks/usePermissions";
import { ProductAnswersSection } from "../../pages/answers/components/ProductAnswersSection";
import { ProductImageButton, ProductImageViewer } from "./ProductImageViewer";
import { LinkedTasks } from "../../pages/tasks/LinkedTasks";
import { useProductQuickSave } from "./quickEdit/useProductQuickSave";
import { InlineEditField } from "./quickEdit/InlineEditField";
import { PricingEditor } from "./quickEdit/PricingEditor";
import { ClassificationEditor } from "./quickEdit/ClassificationEditor";
import { DescriptionEditor } from "./quickEdit/DescriptionEditor";
import { ProductSlugHint } from "./ProductSlugHint";
import { productPageUrl, STOREFRONT_HOST } from "../../config/storefront";
import { PRODUCT_SLUG_MAX, SLUG_RE, cleanSlugInput, productSlugFromName } from "../../utils/slug";

interface ProductDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    product: Product | null;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    onClone?: (id: string) => void;
    onNavigate?: (direction: "prev" | "next") => void;
    hasPrev?: boolean;
    hasNext?: boolean;
    currentIndex?: number;
    totalCount?: number;
    /** Turns on quick edits in the drawer; called with the fresh product
     * after each one, so the screen behind can update too. */
    onProductChanged?: (product: Product) => void;
}

interface InfoRowProps {
    label: string;
    value: React.ReactNode;
    action?: React.ReactNode;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value, action }) => (
    <Box
        sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            py: 0.75,
            gap: 2,
        }}
    >
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: 120, flexShrink: 0 }}
        >
            {label}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: "auto" }}>
            <Typography
                variant="body2"
                fontWeight="medium"
                sx={{ textAlign: "right" }}
            >
                {value ?? <em style={{ opacity: 0.4 }}>—</em>}
            </Typography>
            {action}
        </Box>
    </Box>
);

function movementIcon(type: string) {
    if (type === "SALE" || type === "ADJUSTMENT_DECREASE") return <TrendingDown fontSize="small" color="error" />;
    if (type === "PURCHASE" || type === "ADJUSTMENT_INCREASE" || type === "MANUAL") return <TrendingUp fontSize="small" color="success" />;
    return <SwapVert fontSize="small" color="action" />;
}

export const ProductDetailDrawer: React.FC<ProductDetailDrawerProps> = ({
    open,
    onClose,
    product,
    onEdit,
    onDelete,
    onClone,
    onNavigate,
    hasPrev = false,
    hasNext = false,
    currentIndex,
    totalCount,
    onProductChanged,
}) => {
    const { canWrite, isInvestor } = usePermissions();
    const [stockQty, setStockQty] = useState<number | null>(null);
    // Image open in the full-size viewer (null = closed).
    const [viewerImageId, setViewerImageId] = useState<string | null>(null);
    useEffect(() => {
        setViewerImageId(null);
    }, [product?.product_id]);
    const [isVisible, setIsVisible] = useState<boolean>(true);
    const [togglingVisibility, setTogglingVisibility] = useState(false);
    const [stockDialogOpen, setStockDialogOpen] = useState(false);
    const [reservationsDialogOpen, setReservationsDialogOpen] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity?: "success" | "error" }>({
        open: false,
        message: "",
    });
    const notify = useCallback(
        (message: string, severity: "success" | "error" = "success") => setSnackbar({ open: true, message, severity }),
        [],
    );

    // ── Quick edits ──────────────────────────────────────────────────────
    const handleProductChanged = useCallback(
        (fresh: Product) => onProductChanged?.(fresh),
        [onProductChanged],
    );
    const { save, run } = useProductQuickSave(product?.product_id ?? "", handleProductChanged, notify);
    const quickEdit = canWrite && !!onProductChanged && !!product && !product.is_merged;

    // Which inline editors hold unsaved text — closing or moving to
    // another product asks first, like the order drawer.
    const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(() => new Set());
    const dirtyHandlers = React.useRef(new Map<string, (dirty: boolean) => void>());
    const dirtyFor = useCallback((key: string) => {
        let handler = dirtyHandlers.current.get(key);
        if (!handler) {
            handler = (dirty: boolean) =>
                setDirtyKeys((prev) => {
                    if (prev.has(key) === dirty) return prev;
                    const next = new Set(prev);
                    if (dirty) next.add(key);
                    else next.delete(key);
                    return next;
                });
            dirtyHandlers.current.set(key, handler);
        }
        return handler;
    }, []);
    const confirmDiscardEdits = useCallback(
        () => dirtyKeys.size === 0 || window.confirm("Discard your unsaved change?"),
        [dirtyKeys],
    );
    const requestClose = useCallback(() => {
        if (confirmDiscardEdits()) onClose();
    }, [confirmDiscardEdits, onClose]);
    const requestNavigate = useCallback(
        (direction: "prev" | "next") => {
            if (onNavigate && confirmDiscardEdits()) onNavigate(direction);
        },
        [onNavigate, confirmDiscardEdits],
    );

    // Turning a product off asks first (it disappears from the website,
    // POS and pickers); Featured saves straight away.
    const [confirmInactive, setConfirmInactive] = useState(false);
    const [statusBusy, setStatusBusy] = useState(false);
    const saveStatus = useCallback(
        async (patch: { is_active?: boolean; is_featured?: boolean }, message: string) => {
            setStatusBusy(true);
            const failed = await save(patch, message);
            setStatusBusy(false);
            if (failed) notify(failed, "error");
        },
        [save, notify],
    );
    const [movements, setMovements] = useState<StockMovement[]>([]);
    const [movementsOpen, setMovementsOpen] = useState(false);
    const [movementsLoading, setMovementsLoading] = useState(false);

    useEffect(() => {
        if (product) {
            setStockQty(product.stock?.quantity ?? null);
            setIsVisible(product.is_visible !== false);
            setMovements([]);
            setMovementsOpen(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product?.product_id]); // intentionally keyed on product_id only to reset state when product changes

    useEffect(() => {
        if (!open || !onNavigate) return;
        const handleKey = (e: KeyboardEvent) => {
            // Arrow keys move the cursor while typing in a quick edit.
            const target = e.target as HTMLElement | null;
            if (target?.closest("input, textarea, [contenteditable='true'], [role='combobox'], [role='listbox']")) return;
            if (e.key === "ArrowLeft" && hasPrev) requestNavigate("prev");
            if (e.key === "ArrowRight" && hasNext) requestNavigate("next");
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [open, onNavigate, hasPrev, hasNext, requestNavigate]);

    const handleCopy = useCallback((text: string, label: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setSnackbar({ open: true, message: `${label} copied!` });
        });
    }, []);

    const handleVisibilityToggle = useCallback(async () => {
        if (!product || togglingVisibility) return;
        const newVal = !isVisible;
        setIsVisible(newVal);
        setTogglingVisibility(true);
        try {
            await productService.updateProduct(product.product_id, { is_visible: newVal });
            setSnackbar({ open: true, message: newVal ? "Product visible on website" : "Product hidden from website" });
        } catch {
            setIsVisible(!newVal);
            setSnackbar({ open: true, message: "Failed to update visibility" });
        } finally {
            setTogglingVisibility(false);
        }
    }, [product, isVisible, togglingVisibility]);

    const handleStockSuccess = useCallback((result: StockAdjustmentResult) => {
        setStockQty(result.newQuantity);
        setSnackbar({ open: true, message: `Stock updated to ${result.newQuantity} units` });
    }, []);

    const handleToggleMovements = useCallback(async () => {
        if (!product) return;
        if (!movementsOpen && movements.length === 0) {
            setMovementsLoading(true);
            try {
                const data = await stockService.getStockMovements(product.product_id, 5);
                setMovements(data);
            } catch {
                // ignore
            } finally {
                setMovementsLoading(false);
            }
        }
        setMovementsOpen((prev) => !prev);
    }, [product, movementsOpen, movements.length]);

    if (!product) return null;

    const images = (product.images || []) as ProductImage[];

    const stock = product.stock;
    const qty = stockQty ?? Number(stock?.quantity ?? 0);
    const reserved = Number(stock?.reserved_quantity ?? 0);
    const available = Math.max(qty - reserved, 0);
    const isLowStock =
        stock?.min_stock_level != null && available <= stock.min_stock_level;

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={requestClose}
                PaperProps={{
                    sx: {
                        width: { xs: "100%", sm: 560, md: 620 },
                        maxWidth: "100%",
                        mt: "64px",
                        height: "calc(100% - 64px)",
                    },
                }}
                sx={{ zIndex: 1200 }}
            >
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    {/* ── Header ── */}
                    <Box
                        sx={{
                            p: 2,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                        }}
                    >
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <InlineEditField
                                layout="title"
                                label="Name"
                                display={product.name}
                                raw={product.name}
                                editable={quickEdit}
                                validate={(v) => (v ? (v.length > 200 ? "Keep the name under 200 characters." : null) : "The name can't be empty.")}
                                onSave={(v) => save({ name: v }, "Name saved")}
                                onDirtyChange={dirtyFor("name")}
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 0.75,
                                    mt: 0.5,
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                }}
                            >
                                <Chip
                                    label={product.is_active ? "Active" : "Inactive"}
                                    color={product.is_active ? "success" : "default"}
                                    size="small"
                                />
                                {product.is_featured && (
                                    <Chip label="Featured" color="warning" size="small" />
                                )}
                                {product.is_on_sale && (
                                    <Chip
                                        label={`On Sale −${product.discount_percent}%`}
                                        color="error"
                                        size="small"
                                    />
                                )}
                                <Chip
                                    label={isVisible ? "Visible on Website" : "Hidden from Website"}
                                    color={isVisible ? "primary" : "default"}
                                    size="small"
                                    variant="outlined"
                                />
                                {product.is_merged && (
                                    <Chip
                                        label="Merged"
                                        color="error"
                                        size="small"
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Navigation + close */}
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 0.5,
                                flexShrink: 0,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                {onNavigate && (
                                    <>
                                        <Tooltip title="Previous product (←)">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => requestNavigate("prev")}
                                                    disabled={!hasPrev}
                                                >
                                                    <ChevronLeftIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        {currentIndex !== undefined &&
                                            totalCount !== undefined && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        minWidth: 36,
                                                        textAlign: "center",
                                                        userSelect: "none",
                                                    }}
                                                >
                                                    {currentIndex + 1}/{totalCount}
                                                </Typography>
                                            )}
                                        <Tooltip title="Next product (→)">
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => requestNavigate("next")}
                                                    disabled={!hasNext}
                                                >
                                                    <ChevronRightIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </>
                                )}
                                <IconButton size="small" onClick={requestClose} aria-label="Close">
                                    <CloseIcon />
                                </IconButton>
                            </Box>

                            {/* Action buttons */}
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                                {onClone && (
                                    <Tooltip title="Clone product">
                                        <IconButton
                                            size="small"
                                            color="info"
                                            onClick={() => onClone(product.product_id)}
                                        >
                                            <CloneIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {onEdit && (
                                    <Tooltip title="Edit product">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => onEdit(product.product_id)}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {onDelete && (
                                    <Tooltip title="Delete product">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => onDelete(product.product_id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* ── Quick actions bar ── */}
                    <Box
                        sx={{
                            px: 2,
                            py: 1,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                        }}
                    >
                        {canWrite && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<InventoryIcon fontSize="small" />}
                                onClick={() => setStockDialogOpen(true)}
                            >
                                Adjust Stock
                            </Button>
                        )}
                        <Tooltip title={isVisible ? "Hide from website" : "Show on website"}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={isVisible}
                                        onChange={handleVisibilityToggle}
                                        disabled={togglingVisibility || !canWrite}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {togglingVisibility ? (
                                            <CircularProgress size={12} sx={{ mr: 0.5 }} />
                                        ) : null}
                                        Website
                                    </Typography>
                                }
                                sx={{ m: 0 }}
                            />
                        </Tooltip>
                        {quickEdit && (
                            <>
                                <Tooltip title="Featured products show in the homepage favourites and rank a little higher">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={!!product.is_featured}
                                                onChange={() =>
                                                    void saveStatus(
                                                        { is_featured: !product.is_featured },
                                                        product.is_featured ? "No longer featured" : "Marked as featured",
                                                    )
                                                }
                                                disabled={statusBusy}
                                                size="small"
                                            />
                                        }
                                        label={<Typography variant="body2">Featured</Typography>}
                                        sx={{ m: 0 }}
                                    />
                                </Tooltip>
                                <Tooltip title={product.is_active ? "Turn off: hides it everywhere, including POS" : "Turn back on"}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={product.is_active}
                                                onChange={() =>
                                                    product.is_active
                                                        ? setConfirmInactive(true)
                                                        : void saveStatus({ is_active: true }, "Product turned back on")
                                                }
                                                disabled={statusBusy}
                                                size="small"
                                                color="success"
                                            />
                                        }
                                        label={<Typography variant="body2">Active</Typography>}
                                        sx={{ m: 0 }}
                                    />
                                </Tooltip>
                            </>
                        )}
                    </Box>

                    {/* ── Scrollable content ── */}
                    <Box sx={{ flexGrow: 1, overflow: "auto", p: 3 }}>

                        {/* Image gallery */}
                        {images.length > 0 ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    mb: 3,
                                    overflowX: "auto",
                                    pb: 0.5,
                                }}
                            >
                                {images.map((img) => (
                                    <ProductImageButton
                                        key={img.image_id}
                                        src={img.medium_url}
                                        alt={img.alt_text || product.name}
                                        size={100}
                                        onClick={() => setViewerImageId(img.image_id)}
                                        sx={{
                                            border: img.is_default
                                                ? "2px solid"
                                                : "1px solid",
                                            borderColor: img.is_default
                                                ? "primary.main"
                                                : "divider",
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    mb: 3,
                                    p: 2,
                                    border: "1px dashed",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                }}
                            >
                                <Avatar
                                    variant="rounded"
                                    sx={{ width: 64, height: 64, bgcolor: "grey.100" }}
                                >
                                    <ImageIcon sx={{ color: "grey.400" }} />
                                </Avatar>
                                <Typography variant="body2" color="text.secondary">
                                    No images uploaded
                                </Typography>
                            </Box>
                        )}

                        {/* Pricing & Stock */}
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            PRICING & STOCK
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Grid container spacing={2}>
                                <PricingEditor
                                    product={product}
                                    editable={quickEdit}
                                    save={save}
                                    run={run}
                                    dirtyFor={dirtyFor}
                                />
                                <Grid size={{ xs: 12 }}>
                                    <Divider />
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        In Stock
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {qty}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Reserved
                                    </Typography>
                                    {reserved > 0 ? (
                                        <Tooltip title="View orders holding this reservation">
                                            <Typography
                                                variant="body1"
                                                fontWeight="bold"
                                                color="warning.main"
                                                onClick={() => setReservationsDialogOpen(true)}
                                                sx={{
                                                    cursor: "pointer",
                                                    width: "fit-content",
                                                    "&:hover": { textDecoration: "underline" },
                                                }}
                                            >
                                                {reserved}
                                            </Typography>
                                        </Tooltip>
                                    ) : (
                                        <Typography variant="body1" fontWeight="bold" color="warning.main">
                                            {reserved}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Available
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight="bold"
                                        color={
                                            isLowStock
                                                ? "error.main"
                                                : available > 0
                                                  ? "success.main"
                                                  : "error.main"
                                        }
                                    >
                                        {available}
                                    </Typography>
                                </Grid>
                                {stock?.min_stock_level != null && isLowStock && (
                                    <Grid size={{ xs: 12 }}>
                                        <Chip
                                            label={`Low stock — reorder level: ${stock.min_stock_level}`}
                                            color="error"
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Grid>
                                )}
                            </Grid>

                            {/* Recent stock movements */}
                            <Box sx={{ mt: 1.5 }}>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleToggleMovements}
                                    endIcon={movementsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    sx={{ px: 0, color: "text.secondary", typography: "caption" }}
                                >
                                    {movementsLoading ? "Loading…" : "Recent movements"}
                                </Button>
                                <Collapse in={movementsOpen}>
                                    {movements.length === 0 && !movementsLoading ? (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                            No movements found
                                        </Typography>
                                    ) : (
                                        movements.map((m) => (
                                            <Box
                                                key={m.movement_id}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                    py: 0.5,
                                                    borderBottom: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                {movementIcon(m.movement_type)}
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="caption" display="block" noWrap>
                                                        {m.movement_type.replace(/_/g, " ")}
                                                        {m.notes ? ` — ${m.notes}` : ""}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatDateShort(m.create_time)}
                                                    </Typography>
                                                </Box>
                                                <Typography
                                                    variant="caption"
                                                    fontWeight="bold"
                                                    color={m.quantity >= 0 ? "success.main" : "error.main"}
                                                >
                                                    {m.quantity >= 0 ? "+" : ""}{m.quantity}
                                                </Typography>
                                            </Box>
                                        ))
                                    )}
                                </Collapse>
                            </Box>
                        </Paper>

                        {/* Product details */}
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            PRODUCT DETAILS
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <InlineEditField
                                label="SKU"
                                display={product.sku}
                                raw={product.sku}
                                editable={quickEdit}
                                validate={(v) => (!v ? "The SKU can't be empty." : v.length > 50 ? "Keep it under 50 characters." : null)}
                                onSave={(v) => save({ sku: v }, "SKU saved")}
                                onDirtyChange={dirtyFor("sku")}
                                action={
                                    <Tooltip title="Copy SKU">
                                        <IconButton size="small" onClick={() => handleCopy(product.sku, "SKU")} sx={{ p: 0.25 }}>
                                            <CopyIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                }
                            />
                            {product.slug && (
                                <InlineEditField
                                    label="Web address"
                                    display={
                                        <Box component="span" sx={{ wordBreak: "break-all" }}>
                                            {STOREFRONT_HOST}/products/{product.slug}
                                        </Box>
                                    }
                                    raw={product.slug}
                                    editable={quickEdit}
                                    transform={cleanSlugInput}
                                    validate={(v) =>
                                        !v
                                            ? "Enter a web address."
                                            : v.length > PRODUCT_SLUG_MAX
                                              ? `Keep it under ${PRODUCT_SLUG_MAX} characters.`
                                              : !SLUG_RE.test(v)
                                                ? "Use lowercase letters, numbers and single hyphens."
                                                : null
                                    }
                                    onSave={(v) => save({ slug: v }, "Web address saved — the old one forwards here")}
                                    onDirtyChange={dirtyFor("slug")}
                                    hint={(draft, setDraft) => (
                                        <ProductSlugHint
                                            slug={draft}
                                            productId={product.product_id}
                                            savedSlug={product.slug}
                                            suggestion={productSlugFromName(product.name)}
                                            onUseSuggestion={setDraft}
                                        />
                                    )}
                                    action={
                                        <>
                                            <Tooltip title="Copy link">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCopy(productPageUrl(product.slug!), "Link")}
                                                    sx={{ p: 0.25 }}
                                                    aria-label="Copy link"
                                                >
                                                    <CopyIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={product.is_visible ? "Open on the website" : "Open on the website (hidden from the shop)"}>
                                                <IconButton
                                                    size="small"
                                                    component="a"
                                                    href={productPageUrl(product.slug)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ p: 0.25 }}
                                                    aria-label="Open on the website"
                                                >
                                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    }
                                />
                            )}
                            {(product.barcode || quickEdit) && (
                                <InlineEditField
                                    label="Barcode"
                                    display={product.barcode}
                                    raw={product.barcode ?? ""}
                                    addLabel="Add barcode"
                                    editable={quickEdit}
                                    validate={(v) => (v.length > 50 ? "Keep it under 50 characters." : null)}
                                    onSave={(v) => save({ barcode: v }, v ? "Barcode saved" : "Barcode removed")}
                                    onDirtyChange={dirtyFor("barcode")}
                                    action={
                                        <Tooltip title="Copy barcode">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleCopy(product.barcode!, "Barcode")}
                                                sx={{ p: 0.25 }}
                                            >
                                                <CopyIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                />
                            )}
                            <Divider sx={{ my: 0.5 }} />
                            {(
                                [
                                    ["weight", "Net Weight", "Net weight"],
                                    ["gross_weight", "Gross Weight", "Gross weight"],
                                ] as const
                            ).map(([field, label, words]) => {
                                const value = product[field];
                                return (
                                    <InlineEditField
                                        key={field}
                                        label={label}
                                        display={value != null ? `${Number(value)} kg` : null}
                                        raw={value != null ? String(Number(value)) : ""}
                                        addLabel={`Add ${words.toLowerCase()}`}
                                        type="number"
                                        suffix="kg"
                                        editable={quickEdit}
                                        validate={(v) => (v === "" || Number(v) >= 0 ? null : "Enter a weight in kg.")}
                                        onSave={(v) =>
                                            save({ [field]: v === "" ? null : Number(v) } as any, v === "" ? `${words} removed` : `${words} saved`)
                                        }
                                        onDirtyChange={dirtyFor(field)}
                                    />
                                );
                            })}
                            <InlineEditField
                                label="Dimensions"
                                display={product.dimensions ? `${product.dimensions} cm` : null}
                                raw={product.dimensions ?? ""}
                                addLabel="Add dimensions"
                                placeholder="L x W x H"
                                suffix="cm"
                                editable={quickEdit}
                                validate={(v) => (v.length > 50 ? "Keep it under 50 characters." : null)}
                                onSave={(v) => save({ dimensions: v || (null as any) }, v ? "Dimensions saved" : "Dimensions removed")}
                                onDirtyChange={dirtyFor("dimensions")}
                            />
                            <Divider sx={{ my: 0.5 }} />
                            <InfoRow
                                label="Created"
                                value={
                                    product.create_time
                                        ? formatDateShort(product.create_time)
                                        : null
                                }
                            />
                            <InfoRow
                                label="Updated"
                                value={
                                    product.update_time
                                        ? formatDateShort(product.update_time)
                                        : null
                                }
                            />
                        </Paper>

                        {/* Classification */}
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            CLASSIFICATION
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <ClassificationEditor product={product} editable={quickEdit} save={save} dirtyFor={dirtyFor} />
                        </Paper>

                        {/* Description */}
                        <DescriptionEditor
                            product={product}
                            editable={quickEdit}
                            save={save}
                            onDirtyChange={dirtyFor("description")}
                        />

                        {/* Answer Desk replies for this product (moderators only) */}
                        {!isInvestor && <ProductAnswersSection product={product} open={open} />}

                        {/* Team tasks about this product (staff only) */}
                        <LinkedTasks kind="product" id={product.product_id} label={product.name} heading="caps" />

                        {/* Images metadata */}
                        {images.length > 1 && (
                            <>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    IMAGES ({images.length})
                                </Typography>
                                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                                    {images.map((img, idx) => (
                                        <Box
                                            key={img.image_id}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.5,
                                                py: 0.5,
                                                borderBottom:
                                                    idx < images.length - 1
                                                        ? "1px solid"
                                                        : "none",
                                                borderColor: "divider",
                                            }}
                                        >
                                            <ProductImageButton
                                                src={img.thumbnail_url}
                                                alt={img.alt_text || product.name}
                                                size={36}
                                                onClick={() => setViewerImageId(img.image_id)}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="caption"
                                                    noWrap
                                                    display="block"
                                                >
                                                    {img.original_filename}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {(img.file_size / 1024).toFixed(0)} KB
                                                    {" · "}
                                                    {img.width}×{img.height}
                                                </Typography>
                                            </Box>
                                            {img.is_default && (
                                                <Chip label="Default" size="small" color="primary" />
                                            )}
                                        </Box>
                                    ))}
                                </Paper>
                            </>
                        )}

                        <AuditTrail
                            createdByUserId={product.created_by_user_id}
                            updatedByUserId={product.updated_by_user_id}
                        />
                    </Box>
                </Box>
            </Drawer>

            <StockAdjustmentDialog
                open={stockDialogOpen}
                onClose={() => setStockDialogOpen(false)}
                product={product}
                currentStock={qty}
                onSuccess={handleStockSuccess}
            />

            <ProductImageViewer
                target={
                    viewerImageId
                        ? {
                              productId: product.product_id,
                              productName: product.name,
                              images,
                              startImageId: viewerImageId,
                          }
                        : null
                }
                onClose={() => setViewerImageId(null)}
            />

            <StockReservationsDialog
                open={reservationsDialogOpen}
                onClose={() => setReservationsDialogOpen(false)}
                productId={product.product_id}
                productName={product.name}
                reservedQuantity={reserved}
            />

            <Dialog open={confirmInactive} onClose={() => setConfirmInactive(false)}>
                <DialogTitle>Turn off “{product.name}”?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Inactive products disappear from the website, POS and product pickers. Its stock and order
                        history stay; you can turn it back on any time.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmInactive(false)}>Keep it on</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            setConfirmInactive(false);
                            void saveStatus({ is_active: false }, "Product turned off");
                        }}
                    >
                        Turn off
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={2500}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity ?? "success"}
                    variant="filled"
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};
